import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { MapPin, Clock, Briefcase, ArrowRight, Search } from 'lucide-react';
import { toast } from 'sonner';
import { getJobs, searchJobs } from '../services/Job';
import { SEOHead } from './SEOHead';
import { PageFaq } from './PageAeo';
import { useSiteSettings, getPageSeo, buildFaqSchema } from '../services/SiteSettings';

interface Job {
  id: number;
  title: string;
  department: string;
  location: string;
  type: string;
  description: string;
  qualifications: string[] | string | null;
  education: string;
  responsibilities: string[];
  createdDate: string;
}

interface CareersPageProps {
  onViewJobDetails: (jobId: number, jobTitle: string) => void;
  jobs: Job[];
  loading?: boolean;
}

export function CareersPage({ onViewJobDetails, jobs, loading = false }: CareersPageProps) {
  const settings = useSiteSettings();
  if (loading) {
    return (
      <div className="min-h-screen py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-3 text-gray-600">Loading jobs...</p>
          </div>
        </div>
      </div>
    );
  }
  const [filteredJobs, setFilteredJobs] = useState<Job[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  // const [loading, setLoading] = useState(false);
  const [localJobs, setLocalJobs] = useState<Job[]>([]);

  const jobOrderPriority = settings.careers.jobOrder || [];

  // Sort jobs based on priority order
  const sortJobsByPriority = (jobs: Job[]) => {
    return [...jobs].sort((a, b) => {
      const indexA = jobOrderPriority.indexOf(a.title);
      const indexB = jobOrderPriority.indexOf(b.title);

      // If both jobs are in the priority list, sort by the list order
      if (indexA !== -1 && indexB !== -1) {
        return indexA - indexB;
      }

      // If only job A is in priority list, it comes first
      if (indexA !== -1) return -1;

      // If only job B is in priority list, it comes first  
      if (indexB !== -1) return 1;

      // If neither is in priority list, maintain original order
      return 0;
    });
  };

  // Use jobs from props or fetch locally if not provided
  useEffect(() => {
    if (jobs && jobs.length > 0) {
      const sortedJobs = sortJobsByPriority(jobs);
      setLocalJobs(sortedJobs);
      setFilteredJobs(sortedJobs);
    } else {
      fetchJobs();
    }
  }, [jobs, jobOrderPriority]);

  // Filter jobs when search term changes
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredJobs(localJobs);
    } else {
      const filtered = localJobs.filter(job =>
        job.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.location.toLowerCase().includes(searchTerm.toLowerCase()) ||
        job.description.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredJobs(filtered);
    }
  }, [searchTerm, localJobs]);

  const fetchJobs = async () => {
    try {
      setLoading(true);
      const response = await getJobs();
      const jobsData = response.data || response;
      const sortedJobs = sortJobsByPriority(jobsData);
      setLocalJobs(sortedJobs);
      setFilteredJobs(sortedJobs);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      toast.error('Failed to load jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (searchTerm.trim() === '') {
      setFilteredJobs(localJobs);
      return;
    }

    try {
      setLoading(true);
      const response = await searchJobs(searchTerm);
      const searchResults = response.data || response;
      const sortedResults = sortJobsByPriority(searchResults);
      setFilteredJobs(sortedResults);
    } catch (error) {
      console.error('Error searching jobs:', error);
      toast.error('Failed to search jobs');
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (jobId: number, jobTitle: string) => {
    onViewJobDetails(jobId, jobTitle);
  };

  // Format date function
  const formatPostedDate = (dateString: string) => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return 'Recently';
      }

      const now = new Date();
      const diffTime = Math.abs(now.getTime() - date.getTime());
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

      if (diffDays === 0) {
        return 'Today';
      } else if (diffDays === 1) {
        return '1 day ago';
      } else if (diffDays < 7) {
        return `${diffDays} days ago`;
      } else if (diffDays < 30) {
        const weeks = Math.floor(diffDays / 7);
        return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
      } else {
        return date.toLocaleDateString('en-IN', {
          day: 'numeric',
          month: 'short',
          year: 'numeric'
        });
      }
    } catch (error) {
      return 'Recently';
    }
  };

  // Generate job posting schemas for all jobs
  const jobPostingSchemas = filteredJobs.map((job) => ({
    "@context": "https://schema.org",
    "@type": "JobPosting",
    "title": job.title,
    "description": job.description,
    "datePosted": job.createdDate,
    "hiringOrganization": {
      "@type": "Organization",
      "name": "USP Consultancy",
      "sameAs": "https://uspconsultancy.com.np/",
      "logo": "https://uspconsultancy.com.np/usp.png"
    },
    "jobLocation": {
      "@type": "Place",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": job.location,
        "addressCountry": "Nepal"
      }
    },
    "employmentType": job.type,
    "educationRequirements": job.education,
    "responsibilities": job.responsibilities?.join(', ')
  }));

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://uspconsultancy.com.np/"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Careers",
        "item": "https://uspconsultancy.com.np/careers"
      }
    ]
  };

  const seo = getPageSeo(settings, '/careers');
  const faqSchema = buildFaqSchema(seo.faqs);

  return (
    <div className="min-h-screen py-16">
      <SEOHead
        title={seo.title || 'Careers'}
        description={seo.description}
        canonical="https://uspconsultancy.com.np/careers"
        keywords={seo.keywords}
        takeaways={seo.takeaways}
        structuredData={[breadcrumbSchema, ...jobPostingSchemas, ...(faqSchema ? [faqSchema] : [])]}
      />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="text-center mb-16">
          <h1 className="mb-6">Career Opportunities</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
            Application For Roster of Consultants
          </p>
        </div>

        {/* Search Section */}
        <div className="mb-12">
          <div className="max-w-2xl mx-auto">
            <div className="flex gap-4">
              <div className="flex-1 relative">
                <div className="absolute left-4 inset-y-0 flex items-center justify-center">
                  <Search className="text-gray-400 w-4 h-4" />
                </div>
                <Input
                  type="text"
                  placeholder="Search jobs by title, department, location..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                  className="pl-10"
                />
              </div>
              <Button onClick={handleSearch} disabled={loading} className="cursor-pointer">
                {loading ? 'Searching...' : 'Search'}
              </Button>
            </div>
          </div>
        </div>

        {/* Job Listings */}
        <section>
          <div className="flex items-center justify-between mb-8">
            <h2>Current Openings ({filteredJobs.length})</h2>
          </div>

          {loading ? (
            <div className="text-center py-16">
              <p>Loading jobs...</p>
            </div>
          ) : filteredJobs.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <p className="text-gray-600 mb-4">
                  {searchTerm ? 'No jobs found matching your search.' : 'No current openings at the moment.'}
                </p>
                {searchTerm && (
                  <Button
                    onClick={() => {
                      setSearchTerm('');
                      setFilteredJobs(localJobs);
                    }}
                    className="cursor-pointer"
                  >
                    Clear Search
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-6">
              {filteredJobs.map((job, index) => (
                <JobCard
                  key={job.id}
                  job={job}
                  index={index + 1}
                  onViewDetails={() => handleViewDetails(job.id, job.title)}
                  formatPostedDate={formatPostedDate}
                />
              ))}
            </div>
          )}
        </section>
      </div>
      <div className="mt-16">
        <PageFaq faqs={seo.faqs} />
      </div>
    </div>
  );
}

// Job Card Component
interface JobCardProps {
  job: Job;
  index: number;
  onViewDetails: () => void;
  formatPostedDate: (dateString: string) => string;
}

function JobCard({ job, index, onViewDetails, formatPostedDate }: JobCardProps) {
  // Normalize qualifications (handle string or null from API)
  const qualifications = Array.isArray(job.qualifications)
    ? job.qualifications
    : typeof job.qualifications === 'string'
      ? job.qualifications.split(',').map((q) => q.trim())
      : [];

  return (
    <Card className="hover:shadow-lg transition-shadow">
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div className="flex-1">
            <div className="flex items-start gap-3 mb-3">
              {/* Number indicator instead of colored dot */}
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0 mt-1">
                {index}
              </div>
              <div>
                <CardTitle className="mb-2 font-semibold">{job.title}</CardTitle>
              </div>
            </div>

            {/* <div className="flex flex-wrap gap-4 text-sm text-gray-500 mb-4">
              <div className="flex items-center space-x-1">
                <Clock className="w-4 h-4" />
                <span>Posted {formatPostedDate(job.createdDate)}</span>
              </div>
            </div> */}
          </div>

          <Button onClick={onViewDetails} className="cursor-pointer">
            Apply Now
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </CardHeader>

      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <h4 className="mb-2">Minimum Academic Qualification</h4>
            <p className="text-gray-600 text-sm">{job.education}</p>
          </div>
          <div>
            <h4 className="mb-2">Minimum Experience</h4>
            <ul className="text-gray-600 text-sm space-y-1">
              {qualifications.slice(0, 3).map((qual, idx) => (
                <li key={idx} className="flex items-start space-x-2">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full mt-1.5 flex-shrink-0"></div>
                  <span>{qual}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
